import React, { useState, useEffect } from 'react'
import { ShiftFormModalMetaProps, Duty, ShiftWithDuty, Volunteer, AccessColor } from '../data/types'
import { Button } from './ui/Button'
import { Modal } from './ui/Modal'
import { DeleteIcon, PersonIcon, PhoneIcon, EmailIcon } from '../icons'
import { LeadAssignModal } from './LeadAssignModal'
import { LeadAssignModalMeta, API_BASE_URL } from '../data'


type Props = {
    meta: ShiftFormModalMetaProps
    isOpen: boolean
    onClose: () => void
    onSubmit: (shiftData: {
        duty_id: number,
        start_time: string,
        end_time: string,
        leadName: string,
        leadPhoneNumber: string,
        deputyLeadName: string,
        deputyLeadPhoneNumber: string
        required_volunteer_count: number
    }) => Promise<void>
    duties: Duty[]
    initialShift?: ShiftWithDuty | null
}

export const ShiftFormModal = (props: Props) => {
    const { meta, isOpen, onClose, onSubmit, duties, initialShift } = props

    const [formData, setFormData] = useState({
        duty_id: '',
        start_time: '',
        end_time: '',
        leadName: '',
        leadPhoneNumber: '',
        deputyLeadName: '',
        deputyLeadPhoneNumber: '',
        required_volunteer_count: 0,
        lead_walkie_talkie: false,
        deputy_lead_walkie_talkie: false,
        criteria: '',
        title: '',
        instruction: ''
    })

    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [isLeadAssignModalOpen, setIsLeadAssignModalOpen] = useState(false);
    const [leadTab, setLeadTab] = useState<string | null>(null) // checks whether lead or deputy lead is being selected
    const [leadVolunteer, setLeadVolunteer] = useState<Volunteer | null>(null);
    const [deputyLeadVolunteer, setDeputyLeadVolunteer] = useState<Volunteer | null>(null);
    const [allAccessColors, setAllAccessColors] = useState<AccessColor[]>([])
    const [selectedAccessColor, setSelectedAccessColor] = useState<AccessColor | null>(null);

    
    const fetchAccessColors = async () => {
        try {
            const response = await fetch(`${API_BASE_URL}/access-color/`, { credentials: 'include' })

            if (!response.ok) throw new Error('Failed to fetch access colors.')

            const result = await response.json();

            setAllAccessColors(result)
        }
        catch (err) {
            setError(err instanceof Error ? err.message : "Failed to fetch access colors.")
        }

        console.log("Fetch completed")

    }


    // Populate form when editing a shift or reset when modal closes
    useEffect(() => {

        if (isOpen) fetchAccessColors()

        if (initialShift && isOpen) {
            setFormData({
                duty_id: initialShift.duty_id.toString() ?? '',
                start_time: initialShift.start_time.substring(0, 5) ?? '',
                end_time: initialShift.end_time.substring(0, 5) ?? '',
                leadName: initialShift.lead_name ?? '',
                leadPhoneNumber: initialShift.lead_phone_number ?? '',
                deputyLeadName: initialShift.deputy_lead_name ?? '',
                deputyLeadPhoneNumber: initialShift.deputy_lead_phone_number ?? '',
                required_volunteer_count: initialShift.required_volunteer_count ?? 0,
                lead_walkie_talkie: initialShift.lead ? initialShift.lead_walkie_talkie : false,
                deputy_lead_walkie_talkie: initialShift.deputy_lead ? initialShift.deputy_lead_walkie_talkie : false,
                criteria: initialShift.criteria ? initialShift.criteria : '',
                title: initialShift.title ? initialShift.title : '',
                instruction: initialShift.instruction ? initialShift.instruction : ''
            })
            setLeadVolunteer(initialShift.lead)
            setDeputyLeadVolunteer(initialShift.deputy_lead)

        } else if (!isOpen) {
            setFormData({
                duty_id: '',
                start_time: '',
                end_time: '',
                leadName: '',
                leadPhoneNumber: '',
                deputyLeadName: '',
                deputyLeadPhoneNumber: '',
                required_volunteer_count: 0,
                lead_walkie_talkie: false,
                deputy_lead_walkie_talkie: false,
                criteria: '',
                title: '',
                instruction: ''
            })
            setLeadVolunteer(null)
            setDeputyLeadVolunteer(null)
        }
        setError(null)

    }, [initialShift, isOpen])

    useEffect(() => {
        if (initialShift?.access_color && isOpen) {
            setSelectedAccessColor(initialShift.access_color);
        }

        if (!isOpen) {
            setSelectedAccessColor(null);
        }
    }, [initialShift, isOpen]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value } = e.target
        setFormData(prev => ({ ...prev, [name]: value }))
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        setLoading(true)
        setError(null)

        const duty_name = duties.filter(d => d.id == Number(formData.duty_id))[0].name

        try {
            // Format time fields to ensure they're in HH:MM format (remove seconds if present)
            const formattedData = {
                duty_id: parseInt(formData.duty_id),
                duty_name: duty_name,
                start_time: formData.start_time.substring(0, 5), // Ensure HH:MM format
                end_time: formData.end_time.substring(0, 5),     // Ensure HH:MM format
                leadName: formData.leadName,
                deputyLeadName: formData.deputyLeadName,
                leadPhoneNumber: formData.leadPhoneNumber,
                deputyLeadPhoneNumber: formData.deputyLeadPhoneNumber,
                required_volunteer_count: formData.required_volunteer_count,
                lead_csid: leadVolunteer?.csid ?? null,
                deputy_lead_csid: deputyLeadVolunteer?.csid ?? null,
                access_color_id: selectedAccessColor?.id ?? null,
                lead_walkie_talkie: formData.lead_walkie_talkie,
                deputy_lead_walkie_talkie: formData.deputy_lead_walkie_talkie,
                criteria: formData.criteria,
                title: formData.title,
                instruction: formData.instruction
            }
            await onSubmit(formattedData)
            onClose()
        } catch (err) {
            setError(err instanceof Error ? err.message : meta.errorMessage)
        } finally {
            setLoading(false)
        }
    }

    const handleLeadModalOpen = (tab: string) => {
        setLeadTab(tab)
        setIsLeadAssignModalOpen(true)
    }

    const handleLeadModalClose = () => {
        setLeadTab(null)
        setIsLeadAssignModalOpen(false)
    }

    const handleLeadModalSubmit = async (volunteer: Volunteer) => {
        leadTab === 'lead' ? setLeadVolunteer(volunteer) : setDeputyLeadVolunteer(volunteer)
    }

    const handleRemoveLead = (tab: string) => {
        tab === 'lead' ? setLeadVolunteer(null) : setDeputyLeadVolunteer(null)
    }

    return (
        <>
            <Modal
                isOpen={isOpen}
                onClose={onClose}
                title={meta.title}
                maxWidth="md"
                footer={
                    <div className="flex gap-3 justify-end">
                        <Button
                            type="button"
                            onClick={onClose}
                            disabled={loading}
                            variant="outline"
                            color="neutral"
                        >
                            {meta.cancelButton}
                        </Button>
                        <Button
                            type="submit"
                            form="shift-form"
                            disabled={loading}
                        >
                            {loading ? meta.submittingButton : meta.submitButton}
                        </Button>
                    </div>
                }
            >
                <form id="shift-form" onSubmit={handleSubmit}>
                    <div className="px-6 py-4">
                        {error && (
                            <div className="mb-4 bg-danger-100 dark:bg-danger-900 border border-danger-400 dark:border-danger-700 text-danger-700 dark:text-danger-200 px-4 py-3 rounded">
                                {error}
                            </div>
                        )}

                        <div className="space-y-4">
                            {/* Shift title*/}
                            <div>
                                <label htmlFor="start_time" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    {meta.titleLabel} (Optional)
                                </label>
                                <input
                                    type="text"
                                    placeholder={meta.titlePlaceHolder}
                                    id="title"
                                    name="title"
                                    value={formData.title}
                                    onChange={handleChange}
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-gray-100"
                                />
                            </div>

                            {/* Duty Selection */}
                            <div>
                                <label htmlFor="duty_id" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    {meta.dutyLabel} <span className="text-danger-500">*</span>
                                </label>
                                <select
                                    id="duty_id"
                                    name="duty_id"
                                    required
                                    value={formData.duty_id}
                                    onChange={handleChange}
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-gray-100"
                                >
                                    <option value="">{meta.dutyPlaceholder}</option>
                                    {duties.map((duty) => (
                                        <option key={duty.id} value={duty.id}>
                                            {duty.name}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {/* Time Range */}
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label htmlFor="start_time" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                        {meta.startTimeLabel} <span className="text-danger-500">*</span>
                                    </label>
                                    <input
                                        type="time"
                                        id="start_time"
                                        name="start_time"
                                        required
                                        value={formData.start_time}
                                        onChange={handleChange}
                                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-gray-100"
                                    />
                                </div>
                                <div>
                                    <label htmlFor="end_time" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                        {meta.endTimeLabel} <span className="text-danger-500">*</span>
                                    </label>
                                    <input
                                        type="time"
                                        id="end_time"
                                        name="end_time"
                                        required
                                        value={formData.end_time}
                                        onChange={handleChange}
                                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-gray-100"
                                    />
                                </div>
                                <div>
                                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                        {meta.required_volunteer_count_lable}
                                    </label>
                                    <input
                                        type="number"
                                        id="required_volunteer_count"
                                        name="required_volunteer_count"
                                        value={formData.required_volunteer_count}
                                        onChange={handleChange}
                                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-gray-100"
                                        placeholder={"0"}
                                    />
                                </div>
                            </div>

                            <div>
                                <label
                                    htmlFor="criteria"
                                    className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                                >
                                    {meta.criteriaLabel ?? "Criteria"}
                                </label>

                                <textarea
                                    id="criteria"
                                    name="criteria"
                                    rows={2}
                                    value={formData.criteria}
                                    onChange={(e) =>
                                        setFormData(prev => ({ ...prev, criteria: e.target.value }))
                                    }
                                    placeholder={meta.criteriaPlaceHolder}
                                    className="
                                    w-full px-3 py-2
                                    border border-gray-300 dark:border-gray-600
                                    rounded-md shadow-sm
                                    focus:outline-none focus:ring-2 focus:ring-primary-500
                                dark:bg-gray-700 dark:text-gray-100
                                    resize-y"
                                />
                            </div>

                            <div>
                                <label
                                    htmlFor="instruction"
                                    className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                                >
                                    {meta.instructionLabel ?? "Instructions"}
                                </label>

                                <textarea
                                    id="instruction"
                                    name="instruction"
                                    rows={2}
                                    value={formData.instruction}
                                    onChange={(e) =>
                                        setFormData(prev => ({ ...prev, instruction: e.target.value }))
                                    }
                                    placeholder={meta.instructionPlaceHolder}
                                    className="
                                    w-full px-3 py-2
                                    border border-gray-300 dark:border-gray-600
                                    rounded-md shadow-sm
                                    focus:outline-none focus:ring-2 focus:ring-primary-500
                                dark:bg-gray-700 dark:text-gray-100
                                    resize-y"
                                />
                            </div>


                            {/*Lead and Deputy Lead Details*/}

                            <div className="w-full">

                                <div className='flex justify-between'>
                                    <label
                                        className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                                    >
                                        {meta.leadNameTitle}
                                    </label>
                                    {leadVolunteer && <Button variant='icon' size='sm' color='danger' onClick={() => handleRemoveLead('lead')}>
                                        <DeleteIcon className="w-4 h-4" />
                                    </Button>}
                                </div>

                                <div
                                    className="flex items-center justify-between w-full
                                                                                        rounded-md border border-gray-300 dark:border-gray-600
                                                                                         bg-white dark:bg-gray-800
                                                                                        px-3 py-2"
                                >
                                    {(leadVolunteer ?
                                        <div className='text-gray-900 dark:text-white'>
                                            <div className="flex items-center gap-2">
                                                <PersonIcon />
                                                <p>{`${leadVolunteer.first_name} ${leadVolunteer.last_name}`}</p>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <EmailIcon />
                                                <p>{`${leadVolunteer.email}`}</p>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <PhoneIcon />
                                                <p>{`${leadVolunteer.phone}`}</p>
                                            </div>
                                        </div>
                                        :
                                        <span className="text-sm text-gray-500 dark:text-gray-400">
                                            No Lead Allocated
                                        </span>)

                                    }

                                    <Button
                                        type="button"
                                        color="primary"
                                        size="sm"
                                        onClick={() => { handleLeadModalOpen('lead') }}
                                    >
                                        Select
                                    </Button>
                                </div>

                            </div>

                            {leadVolunteer && (<div >
                                <label className='text-md text-gray-600 dark:text-gray-300 me-2 italic'>{meta.require_walkie_talkie_label}</label>
                                <input
                                    type="checkbox"
                                    name="lead_walkie_talkie"
                                    checked={formData.lead_walkie_talkie}
                                    onChange={(e) =>
                                        setFormData(prev => ({
                                            ...prev,
                                            lead_walkie_talkie: e.target.checked,
                                        }))
                                    }
                                />
                            </div>)}


                            <div className="w-full">
                                <div className='flex justify-between'>
                                    <label
                                        className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                                    >
                                        {meta.deputyLeadNameTitle}
                                    </label>
                                    {deputyLeadVolunteer && <Button variant='icon' size='sm' color='danger' onClick={() => handleRemoveLead('deputy_lead')}>
                                        <DeleteIcon className="w-4 h-4" />
                                    </Button>}
                                </div>

                                <div
                                    className="flex items-center justify-between w-full
                                                                                        rounded-md border border-gray-300 dark:border-gray-600
                                                                                         bg-white dark:bg-gray-800
                                                                                        px-3 py-2"
                                >
                                    {(deputyLeadVolunteer ?
                                        <div className='text-gray-900 dark:text-white'>
                                            <div className="flex items-center gap-2">
                                                <PersonIcon />
                                                <p>{`${deputyLeadVolunteer.first_name} ${deputyLeadVolunteer.last_name}`}</p>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <EmailIcon />
                                                <p>{`${deputyLeadVolunteer.email}`}</p>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <PhoneIcon />
                                                <p>{`${deputyLeadVolunteer.phone}`}</p>
                                            </div>
                                        </div>
                                        :
                                        <span className="text-sm text-gray-500 dark:text-gray-400">
                                            No Deputy Lead Allocated
                                        </span>)
                                    }

                                    <Button
                                        type="button"
                                        color="primary"
                                        size="sm"
                                        onClick={() => { handleLeadModalOpen('deputy_lead') }}
                                    >
                                        Select
                                    </Button>
                                </div>
                            </div>

                            {deputyLeadVolunteer && (<div className='mt-3'>
                                <label className='text-md text-gray-600 dark:text-gray-300 me-2 italic'>{meta.require_walkie_talkie_label}</label>
                                <input
                                    type="checkbox"
                                    name="lead_walkie_talkie"
                                    checked={formData.deputy_lead_walkie_talkie}
                                    onChange={(e) =>
                                        setFormData(prev => ({
                                            ...prev,
                                            deputy_lead_walkie_talkie: e.target.checked,
                                        }))
                                    }
                                />
                            </div>)}

                            {/* Access Color */}

                            <div className="w-full">
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    {meta.accessColorLabel ?? 'Access Color'}
                                </label>

                                <p className='text-sm text-gray-400 mb-2 italic'>Leave this field empty if the access color is same as the area.</p>

                                <select
                                    value={selectedAccessColor?.id ?? ''}
                                    onChange={(e) => {
                                        const selected = allAccessColors.find(
                                            c => c.id === Number(e.target.value)
                                        ) || null;
                                        setSelectedAccessColor(selected);
                                    }}
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600
                                        rounded-md shadow-sm focus:outline-none focus:ring-2
                                        focus:ring-primary-500 dark:bg-gray-700 dark:text-gray-100"

                                    style={{ backgroundColor: selectedAccessColor ? selectedAccessColor.color : "#FFFFFF" }}
                                >
                                    <option value="">Select an access color</option>

                                    {allAccessColors.map((ac, index) => (
                                        <option key={index} value={ac.id} style={{ backgroundColor: ac.color }}>
                                            {ac.color}
                                        </option>
                                    ))}
                                </select>
                            </div>


                            {/* <div className='space-y-2'>
                            <label htmlFor="lead-name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                {meta.leadNameTitle}
                            </label>
                            <input
                                type="text"
                                id="lead-name"
                                name="leadName"

                                value={formData.leadName}
                                onChange={handleChange}
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-gray-100"
                                placeholder={meta.leadNamePlaceholder}
                            />

                            <label htmlFor="lead-phone-number" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                {meta.leadcontactTitle}
                            </label>
                            <input
                                type="text"
                                id="lead-phone-number"
                                name="leadPhoneNumber"

                                value={formData.leadPhoneNumber}
                                onChange={handleChange}
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-gray-100"
                                placeholder={meta.leadPhoneNumberPlaceHolder}
                            />

                            <label htmlFor="name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                {meta.deputyLeadNameTitle}
                            </label>
                            <input
                                type="text"
                                id="deputy-lead-name"
                                name="deputyLeadName"

                                value={formData.deputyLeadName}
                                onChange={handleChange}
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-gray-100"
                                placeholder={meta.deputyLeadNamePlaceHolder}
                            />

                            <label htmlFor="name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                {meta.deputyLeadcontactTitle}
                            </label>
                            <input
                                type="text"
                                id="deputy-lead-phone-number"
                                name="deputyLeadPhoneNumber"

                                value={formData.deputyLeadPhoneNumber}
                                onChange={handleChange}
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-gray-100"
                                placeholder={meta.deputyLeadPhoneNumberPlaceHolder}
                            />
                        </div> */}

                        </div>
                    </div>
                </form>
            </Modal>

            <LeadAssignModal
                meta={LeadAssignModalMeta}
                title={leadTab === 'lead' ? "Allocate Lead" : "Allocate Deputy Lead"}
                isOpen={isLeadAssignModalOpen}
                onClose={handleLeadModalClose}
                onSubmit={handleLeadModalSubmit}
                currentLead={leadTab === 'lead' ? leadVolunteer : deputyLeadVolunteer}
            />
        </>
    )
}
